import axios from 'axios';
import { getToken, clearAuth } from '../utils/auth';

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

api.interceptors.request.use(config => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  response => response.data,
  error => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    if (status === 401 && !url.includes('/auth/login')) {
      clearAuth();
      window.location.href = '/login';
      return Promise.reject(new Error('登录已过期，请重新登录'));
    }
    if (status === 403) {
      // 权限不足由 AuthGuard 组件处理，不自动跳转
    }
    const msg = error.response?.data?.message || error.message || '请求失败';
    return Promise.reject(new Error(msg));
  }
);

export default api;

// ── Auth ──
export const login = (data) => api.post('/auth/login', data);
export const getMenus = () => api.get('/auth/menus');
export const changePassword = (data) => api.put('/auth/change-password', data);
export const getCaptcha = () => api.get('/auth/captcha');

// ── Users ──
export const getUsers = (params) => api.get('/users', { params });
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const setUserStatus = (id, status) => api.put(`/users/${id}/status`, { status });
export const resetUserPassword = (id) => api.put(`/users/${id}/reset-password`);

// ── Roles ──
export const getRoles = () => api.get('/roles');
export const createRole = (data) => api.post('/roles', data);
export const updateRole = (id, data) => api.put(`/roles/${id}`, data);
export const deleteRole = (id) => api.delete(`/roles/${id}`);
export const getRolePages = (id) => api.get(`/roles/${id}/pages`);
export const setRolePages = (id, pageIds) => api.put(`/roles/${id}/pages`, { pageIds });

// ── Page Resources ──
export const getPageTree = () => api.get('/page-resources/tree');

// ── Operation Logs ──
export const getOperationLogs = (params) => api.get('/operation-logs', { params });

// ── Sales Teams ──
export const getSalesTeams = (params) => api.get('/sales/teams', { params });
export const createSalesTeam = (data) => api.post('/sales/teams', data);
export const updateSalesTeam = (id, data) => api.put(`/sales/teams/${id}`, data);
export const deleteSalesTeam = (id) => api.delete(`/sales/teams/${id}`);

// ── Salespersons ──
export const getSalespersons = (params) => api.get('/sales/persons', { params });
export const createSalesperson = (data) => api.post('/sales/persons', data);
export const updateSalesperson = (id, data) => api.put(`/sales/persons/${id}`, data);
export const deleteSalesperson = (id) => api.delete(`/sales/persons/${id}`);

// ── Products ──
export const getProducts = (params) => api.get('/sales/products', { params });
export const createProduct = (data) => api.post('/sales/products', data);
export const updateProduct = (id, data) => api.put(`/sales/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/sales/products/${id}`);

// ── Tasks ──
export const getSalesTasks = (params) => api.get('/sales/tasks', { params });
export const createSalesTask = (data) => api.post('/sales/tasks', data);
export const updateSalesTask = (id, data) => api.put(`/sales/tasks/${id}`, data);
export const batchCreateTasks = (tasks) => api.post('/sales/tasks/batch', { tasks });
export const deleteSalesTask = (id) => api.delete(`/sales/tasks/${id}`);

// ── Performances ──
export const getPerformances = (params) => api.get('/sales/performances', { params });
export const updatePerformance = (id, data) => api.put(`/sales/performances/${id}`, data);
export const upsertPerformance = (data) => api.post('/sales/performances/upsert', data);

// ── Expenses ──
export const calculateExpense = (params) => api.get('/sales/expenses/calculate', { params });
export const createExpense = (data) => api.post('/sales/expenses', data);
export const deleteExpense = (id) => api.delete(`/sales/expenses/${id}`);

// ── Reports ──
export const getTeamSummary = (params) => api.get('/sales/reports/team-summary', { params });
export const getPersonSummary = (params) => api.get('/sales/reports/person-summary', { params });
export const getExpenseStructure = (params) => api.get('/sales/reports/expense-structure', { params });
export const getExpenseTrend = (params) => api.get('/sales/reports/trend', { params });
export const getExpenseList = (params) => api.get('/sales/reports/expense-list', { params });
export const getSampleDetail = (params) => api.get('/sales/reports/sample-detail', { params });
export const exportSampleDetail = (params) => api.get('/sales/reports/sample-detail/export', { params, responseType: 'blob' });
export const exportPersonSummary = (params) => api.get('/sales/reports/person-summary/export', { params, responseType: 'blob' });

// ── Sales Orders ──
export const getSalesOrders = (params) => api.get('/sales/orders', { params });
export const getSalesOrderDetail = (soId) => api.get(`/sales/orders/${soId}`);
export const getSalesPlatforms = () => api.get('/sales/orders/sites');

// ── Sales Task Years ──
export const getSalesTaskYears = () => api.get('/sales/task-years');
export const getCurrentTaskYear = (year) => api.get('/sales/task-years/current', { params: { year } });
export const updateTaskYear = (id, data) => api.post(`/sales/task-years/${id}`, data);
export const publishTaskYear = (id) => api.post(`/sales/task-years/${id}/publish`);
export const unpublishTaskYear = (id) => api.post(`/sales/task-years/${id}/unpublish`);
export const generateQuarters = (id) => api.post(`/sales/task-years/${id}/generate-quarters`);

// ── Sales Task Team Months ──
export const getTaskTeamMonths = (params) => api.get('/sales/task-team-months', { params });
export const batchSaveTaskTeamMonths = (items) => api.post('/sales/task-team-months/batch', { items });

// ── Sales Task Stats ──
export const getAnnualTaskStats = (year) => api.get('/sales/task-stats/annual', { params: { year } });



// ── 昨日汇总 ──
export const getYesterdaySummary = (date) => api.get('/sales/yesterday/summary', { params: date ? { date } : {} });
export const getYesterdayProductDetail = (date) => api.get('/sales/yesterday/product-detail', { params: date ? { date } : {} });
export const getYesterdayOnlineProductDetail = (date) => api.get('/sales/yesterday/online-product-detail', { params: date ? { date } : {} });

// ── BI 驾驶舱 ──
export const getDashboardBiAll = () => api.get('/dashboard/bi/all');

// ── 采购成本分析 ──
export const getPurchaseCostOptions = () => api.get('/purchase/cost/options');
export const getPurchaseCostSummary = (params) => api.get('/purchase/cost/summary', { params });
export const getPurchaseCostMaterials = (params) => api.get('/purchase/cost/materials', { params });
export const getPurchaseCostSuppliers = (params) => api.get('/purchase/cost/suppliers', { params });
export const getPurchaseCostOrders = (params) => api.get('/purchase/cost/orders', { params });
export const getPurchaseOrderLines = (billNo) => api.get(`/purchase/cost/orders/${billNo}/lines`);
export const getPurchaseMaterialComparison = (params) => api.get('/purchase/cost/material-comparison', { params });
export const exportPurchaseCost = (params) => api.get('/purchase/cost/export', { params, responseType: 'blob' });
